// pages/DwiSendHub.jsx
// /dwisend-drc — the one DwiSend page for DRC clients, like Uganda's DwiSend
// page with its tabs: Boutique · Mes commandes · Demandes (?tab=shop|orders|requests).
// Boutique and Mes commandes are there only when the shop is open for this
// account (the server decides — same check as before); until then the page
// shows Demandes only. Old /dwisend-shop and /dwisend-requests links (also in
// notifications) forward to the matching tab.
import { useState, useEffect, useCallback } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { dwisendShopAPI, dwisendRequestAPI } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { Spinner } from '../components/ui';
import { t, langFor, LANGS } from '../i18n/dwisend';
import { DRC_HOME } from '../components/layout/regionalNav';
import DwiSendShop from './DwiSendShop';
import DwiSendRequests from './DwiSendRequests';

export const TAB_ORDER = ['shop', 'orders', 'requests'];

// Which tabs this account gets, and the one to show for ?tab=.
export function hubTabs(shopOpen, asked) {
  const tabs = shopOpen ? TAB_ORDER : ['requests'];
  return { tabs, tab: tabs.includes(asked) ? asked : tabs[0] };
}

export default function DwiSendHub() {
  const { user, updateUser } = useAuth();
  const [lang, setLang] = useState(() => langFor(user));
  const [params, setParams] = useSearchParams();
  const [shopOpen, setShopOpen] = useState(null);   // null = still asking the server
  const [unpaid, setUnpaid] = useState(0);
  const T = (k, v) => t(lang, k, v);

  useEffect(() => { dwisendShopAPI.status().then(r => setShopOpen(!!r.available)).catch(() => setShopOpen(false)); }, []);
  const go = useCallback(k => setParams({ tab: k }), [setParams]);
  const switchLang = async l => {
    updateUser({ language: l });
    await dwisendRequestAPI.setLanguage(l).catch(() => {});
    setLang(l);
  };

  if (shopOpen === null) return <div className="py-16 flex justify-center"><Spinner /></div>;
  const { tabs, tab } = hubTabs(shopOpen, params.get('tab'));
  const label = { shop: T('shop.tab.shop'), orders: `${T('shop.tab.orders')}${unpaid ? ` (${unpaid})` : ''}`, requests: T('hub.tab.requests') };

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-black text-text-primary">DwiSend</h1>
          <p className="text-sm text-text-secondary mt-1 max-w-2xl">{T(tab === 'requests' ? 'req.subtitle' : 'shop.subtitle')}</p>
        </div>
        <div className="flex items-center gap-1 text-xs" aria-label={T('req.lang')}>
          {LANGS.map(l => (
            <button key={l} onClick={() => switchLang(l)}
              className={`px-2.5 py-1 rounded-lg font-bold ${lang === l ? 'bg-cyan/20 text-cyan' : 'text-text-muted hover:text-text-primary'}`}>
              {l === 'fr' ? 'Français' : 'English'}
            </button>
          ))}
        </div>
      </div>
      {tabs.length > 1 && (
        <div className="flex gap-2 flex-wrap" role="tablist">
          {tabs.map(k => (
            <button key={k} role="tab" aria-selected={tab === k} onClick={() => go(k)}
              className={`px-3 py-1.5 rounded-lg text-sm font-semibold ${tab === k ? 'bg-cyan/20 text-cyan' : 'text-text-muted hover:text-text-primary'}`}>{label[k]}</button>
          ))}
        </div>
      )}
      {tab === 'requests'
        ? <DwiSendRequests embedded lang={lang} />
        : <DwiSendShop embedded lang={lang} tab={tab} onTab={go} onUnpaid={setUnpaid} />}
    </div>
  );
}

// Old addresses (bookmarks, notification links) → the matching tab.
export function DrcLegacyRedirect({ tab }) {
  return <Navigate to={`${DRC_HOME}?tab=${tab}`} replace />;
}
